import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress, useTheme } from "@mui/material";
import {
    SovosBox,
    SovosButton,
    SovosTooltip,
    SovosTypography,
} from "mosaic-react";
import { AutoAwesome, CheckCircle } from "mosaic-react-icons";
import { DataGrid, rows } from "colbi_web_ui/lib/components";
import { APRS_WEBAPP_SOVI_AI_ENABLED } from "./featureFlags";

const StatTile = ({ label, value, borderColor, hint }) => (
    <SovosBox
        sx={{
            flex: 1,
            minWidth: 140,
            p: 1.5,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            borderLeft: "4px solid",
            borderLeftColor: borderColor,
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
            bgcolor: "background.paper",
        }}
    >
        <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>{label}</SovosTypography>
        <SovosTypography variant="h6" sx={{ fontWeight: 700 }}>{value}</SovosTypography>
        {hint && <SovosTypography variant="caption" sx={{ color: "text.disabled" }}>{hint}</SovosTypography>}
    </SovosBox>
);

const StagingDataGrid = ({
    items = [],
    columnHeaders = [],
    issueSummary = null,
    loading = false,
    savingCell = false,
    profileId,
    projectId,
    canMutate = true,
    nonOwnerTooltip,
    onFixCell,
    onRefreshIssues,
    onProceed,
    proceedDisabled = false,
    proceedLabel = "Proceed data",
}) => {
    const theme = useTheme();
    const [showErrorsOnly, setShowErrorsOnly] = useState(false);
    const [showAiSuggestion, setShowAiSuggestion] = useState(false); // eslint-disable-line no-unused-vars
    const [rowOverrides, setRowOverrides] = useState(new Map());
    const [isSavingLocal, setIsSavingLocal] = useState(false);

    useEffect(() => {
        setRowOverrides(new Map());
    }, [items]);

    const byColumn = useMemo(() => issueSummary?.by_column || {}, [issueSummary]);

    const allRowsCount = issueSummary?.affected_rows != null
        ? items.length
        : items.length;
    const totalErrors = issueSummary?.total_errors ?? 0;
    const affectedRows = issueSummary?.affected_rows ?? 0;
    const validRows = Math.max(0, allRowsCount - affectedRows);

    const issuesByClbId = useMemo(() => {
        const map = new Map();
        const rowsArr = issueSummary?.rows || [];
        for (const row of rowsArr) {
            map.set(row.clb_id, row.issues || []);
        }
        return map;
    }, [issueSummary]);

    const affectedClbIds = useMemo(() => {
        if (!showErrorsOnly) return null;
        const ids = new Set();
        const rowsArr = issueSummary?.rows || [];
        for (const row of rowsArr) {
            if ((row.issues || []).some((i) => i.severity === "error")) {
                ids.add(row.clb_id);
            }
        }
        return ids;
    }, [issueSummary, showErrorsOnly]);

    const effectiveItems = useMemo(() => {
        if (rowOverrides.size === 0 && !affectedClbIds) return items;
        return items
            .map((item) => {
                const id = item.CLB_ID ?? item.clb_id;
                const override = rowOverrides.get(String(id));
                return override ? { ...item, ...override } : item;
            })
            .filter((item) => {
                if (!affectedClbIds) return true;
                return affectedClbIds.has(item.CLB_ID ?? item.clb_id);
            });
    }, [items, rowOverrides, affectedClbIds]);

    const columnHeaderRenderer = useCallback((headerItem) => {
        const colName = headerItem.columnName;
        const stats = byColumn[colName] || byColumn[colName?.replace(/^CDM_[^.]+\./, "")] || null;
        const errorCount = stats?.errors || 0;
        const warningCount = stats?.warnings || 0;
        const hasIssues = errorCount > 0 || warningCount > 0;

        return (
            <SovosBox sx={{ display: "flex", flexDirection: "column", width: "100%", py: 0.5, gap: 0.5 }}>
                <SovosBox sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {errorCount > 0 && (
                        <SovosBox sx={{
                            display: "inline-flex",
                            alignSelf: "flex-start",
                            alignItems: "center",
                            px: 0.75,
                            py: 0.125,
                            borderRadius: 0.5,
                            bgcolor: "error.light",
                        }}>
                            <SovosTypography variant="caption" sx={{ color: "error.main", fontWeight: 600, fontSize: 10 }}>
                                {errorCount} error{errorCount !== 1 ? "s" : ""}
                            </SovosTypography>
                        </SovosBox>
                    )}
                    {warningCount > 0 && (
                        <SovosBox sx={{
                            display: "inline-flex",
                            alignSelf: "flex-start",
                            alignItems: "center",
                            px: 0.75,
                            py: 0.125,
                            borderRadius: 0.5,
                            bgcolor: "warning.light",
                        }}>
                            <SovosTypography variant="caption" sx={{ color: "warning.dark", fontWeight: 600, fontSize: 10 }}>
                                {warningCount} warning{warningCount !== 1 ? "s" : ""}
                            </SovosTypography>
                        </SovosBox>
                    )}
                    {!hasIssues && (
                        <SovosBox sx={{
                            display: "inline-flex",
                            alignSelf: "flex-start",
                            alignItems: "center",
                            px: 0.75,
                            py: 0.125,
                            borderRadius: 0.5,
                            bgcolor: "success.light",
                        }}>
                            <SovosTypography variant="caption" sx={{ color: "success.dark", fontWeight: 600, fontSize: 10 }}>
                                0 issues
                            </SovosTypography>
                        </SovosBox>
                    )}
                </SovosBox>
                <SovosTypography variant="caption" sx={{ fontWeight: 600 }} noWrap>
                    {headerItem.headerName || colName}
                </SovosTypography>
            </SovosBox>
        );
    }, [byColumn]);

    const cellHighlight = useCallback((params) => {
        const clbId = params.row?.CLB_ID ?? params.row?.clb_id;
        if (clbId == null) return "";
        const cellIssues = issuesByClbId.get(clbId) || [];
        const rawField = params.field;
        const colIssues = cellIssues.filter(
            (issue) =>
                issue.column === rawField ||
                `CDM_CustomCSV.${issue.column}` === rawField,
        );
        if (colIssues.length === 0) return "";
        return colIssues.some((i) => i.severity === "error")
            ? "staging-error-cell"
            : "staging-warning-cell";
    }, [issuesByClbId]);

    const handleRowEdit = useCallback(async (newRow, oldRow) => {
        if (!canMutate) return oldRow;
        const changedCol = columnHeaders
            .map((h) => h.columnName)
            .find((col) => newRow[col] !== oldRow[col]);
        if (!changedCol) return newRow;
        const clbId = newRow.CLB_ID ?? newRow.clb_id ?? newRow["CDM_CustomCSV.CLB_ID"];
        const bareColumn = changedCol.replace(/^CDM_[^.]+\./, "");
        if (!onFixCell || !profileId || !projectId || clbId == null) return oldRow;
        setIsSavingLocal(true);
        try {
            const result = await onFixCell(profileId, String(projectId), clbId, bareColumn, newRow[changedCol] ?? null);
            if (!result?.success) return oldRow;
            setRowOverrides((prev) => {
                const next = new Map(prev);
                next.set(String(clbId), newRow);
                return next;
            });
            if (onRefreshIssues) onRefreshIssues();
            return newRow;
        } catch (_) {
            return oldRow;
        } finally {
            setIsSavingLocal(false);
        }
    }, [canMutate, columnHeaders, onFixCell, profileId, projectId, onRefreshIssues]);

    const dgHeader = useMemo(
        () => columnHeaders.map((h) => ({
            ...h,
            label: [h.headerName || h.columnName],
            sortable: false,
            editable: canMutate,
        })),
        [columnHeaders, canMutate],
    );

    const fixByAiLabel = APRS_WEBAPP_SOVI_AI_ENABLED ? "Fix by AI" : "Fix by AI";

    return (
        <SovosBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SovosBox sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <StatTile label="All rows" value={allRowsCount} borderColor="primary.main" />
                <StatTile label="Valid rows" value={validRows} borderColor="success.main" />
                <StatTile label="Errors" value={totalErrors} borderColor="error.main" />
                <StatTile
                    label="Suggested fixes"
                    value={0}
                    borderColor="warning.main"
                    hint="Available with Sovi AI"
                />
            </SovosBox>

            <SovosBox sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
            }}>
                <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SovosButton
                        size="small"
                        variant={showErrorsOnly ? "contained" : "outlined"}
                        color={showErrorsOnly ? "error" : "primary"}
                        onClick={() => setShowErrorsOnly((v) => !v)}
                        disabled={!issueSummary || totalErrors === 0}
                    >
                        Show Errors
                    </SovosButton>
                    <SovosTooltip title="AI assistance ships in a follow-up ticket." placement="top">
                        <span>
                            <SovosButton size="small" variant="outlined" disabled>
                                Show AI suggestion
                            </SovosButton>
                        </span>
                    </SovosTooltip>
                    {(loading || savingCell || isSavingLocal) && (
                        <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <CircularProgress size={12} />
                            <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>
                                {loading ? "Scanning…" : "Saving…"}
                            </SovosTypography>
                        </SovosBox>
                    )}
                </SovosBox>

                <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SovosTooltip title="AI assistance ships in a follow-up ticket." placement="top">
                        <span>
                            <SovosButton
                                size="small"
                                variant="outlined"
                                startIcon={<AutoAwesome sx={{ fontSize: 14 }} />}
                                disabled
                            >
                                {fixByAiLabel}
                            </SovosButton>
                        </span>
                    </SovosTooltip>
                    <SovosTooltip title={canMutate ? "" : nonOwnerTooltip} placement="top">
                        <span>
                            <SovosButton
                                size="small"
                                variant="contained"
                                startIcon={<CheckCircle sx={{ fontSize: 14 }} />}
                                onClick={onProceed}
                                disabled={proceedDisabled || !onProceed || !canMutate}
                            >
                                {proceedLabel}
                            </SovosButton>
                        </span>
                    </SovosTooltip>
                </SovosBox>
            </SovosBox>

            <DataGrid
                id="staging-data-grid"
                items={effectiveItems}
                header={dgHeader}
                card={rows.CellCard}
                status={{ loading: loading }}
                metadata={{ total: effectiveItems.length }}
                enablePagination
                headerHeight={70}
                columnHeaderRenderer={columnHeaderRenderer}
                showCheckbox
                editMode={canMutate}
                onRowEdit={handleRowEdit}
                onCellHighlight={cellHighlight}
                modifierssx={{
                    "& .MuiDataGrid-columnHeader": { alignItems: "flex-start", pt: 1 },
                    "& .MuiDataGrid-columnHeaderTitle": { whiteSpace: "normal" },
                    "& .staging-warning-cell": {
                        backgroundColor: `${theme.palette.warning.light} !important`,
                        borderLeft: `3px solid ${theme.palette.warning.main} !important`,
                    },
                    "& .staging-error-cell": {
                        backgroundColor: `${theme.palette.error.light} !important`,
                        borderLeft: `3px solid ${theme.palette.error.main} !important`,
                    },
                }}
            />
        </SovosBox>
    );
};

export default StagingDataGrid;
